import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouteContext } from '@tanstack/react-router';
import type { SalePreviewEntry } from '@ooh/contracts';
import { offerQueries } from './queries';
import type { WithCallWithAuth } from './queries';

function useEffectiveAuth(): WithCallWithAuth {
  const { auth } = useRouteContext({ from: '__root__' });
  // RouterContext['auth'] is structurally identical to WithCallWithAuth but TS cannot
  // infer the narrowing from useRouteContext's return type — cast is safe, mirroring
  // mutations.ts's own useEffectiveAuth.
  return auth as WithCallWithAuth;
}

export interface SalePreviewData {
  acceptedCount: number;
  notCurrentlyAcceptedCount: number;
  rows: SalePreviewEntry[];
}

export interface UseSalePreviewResult {
  isLoading: boolean;
  isError: boolean;
  data: SalePreviewData | undefined;
}

/**
 * GET /offers/:id/sale-preview (campaign-sale / "Sale preview") — powers the
 * record-sale dialog's breakdown: how many distinct nośniki this offer's
 * active reservations affect are currently accepted vs not, and the full
 * per-nośnik row list to render. The preview response is already
 * deduplicated by nośnik server-side (one row per mediaUnitId, every window
 * listed in `periods`) — no client-side dedup needed here.
 */
export function useSalePreview(offerId: string): UseSalePreviewResult {
  const auth = useEffectiveAuth();
  const query = useQuery(offerQueries.salePreview(auth, offerId));

  const data = React.useMemo<SalePreviewData | undefined>(() => {
    if (query.data === undefined) return undefined;
    const acceptedCount = query.data.filter((entry) => entry.currentlyAccepted).length;
    return {
      acceptedCount,
      notCurrentlyAcceptedCount: query.data.length - acceptedCount,
      rows: query.data,
    };
  }, [query.data]);

  return { isLoading: query.isLoading, isError: query.isError, data };
}
